/**
 * ComputeEvaluator — safe recursive-descent expression evaluator.
 * Port of Flutter's CogoComputeEvaluator (805 LOC Dart → TypeScript).
 *
 * NO `new Function()`, NO `eval()`. Pure string parsing only.
 * Supports: literals, #path refs, ternary, comparison, arithmetic (with precedence),
 * array methods (filter/find/reduce/map/unique), Math.*, formatCurrency, predicates.
 */

import { ExpressionCache, type CacheStats, type CachedExpression } from "./expression-cache"

export interface StateAccessor {
  get(path: string): unknown
  resolve(template: string): unknown
}

export class ComputeEvaluator {
  private state: StateAccessor
  private cache = new ExpressionCache()

  constructor(state: StateAccessor) {
    this.state = state
  }

  /** Return cache performance statistics. */
  getCacheStats(): CacheStats {
    return this.cache.getStats()
  }

  /** Clear all cached expression classifications. */
  clearCache(): void {
    this.cache.clear()
  }

  /** Main entry point — evaluate a compute expression string. */
  evaluate(expression: string): unknown {
    let trimmed = expression.trim()

    // 0. Unwrap #{...} wrapper if present (non-greedy: only match single #{...} pattern)
    const pureMatch = /^#\{([^}]+)\}$/.exec(trimmed)
    if (pureMatch) {
      trimmed = pureMatch[1].trim()
    }

    // Cache check — skip regex chain on hit
    const cached = this.cache.get(trimmed)
    if (cached) {
      return this.executeCached(cached, trimmed)
    }

    return this.evaluateAndCache(trimmed)
  }

  /**
   * Execute a cached expression classification against current state.
   * The cache stores WHAT kind of expression it is, not the result value.
   */
  private executeCached(cached: CachedExpression, expr: string): unknown {
    switch (cached.type) {
      case "string-literal":
        return expr.substring(1, expr.length - 1)
      case "number-literal":
        return cached.value
      case "boolean":
        return cached.value
      case "null-literal":
        return null
      case "simple-path":
        return this.state.get(expr.substring(1))
      case "dotted-path":
        return this.state.get(expr)
      case "length-compare": {
        const m = cached.meta as { path: string; op: string; val: number }
        const arr = this.state.get(m.path)
        const len = Array.isArray(arr) ? arr.length : 0
        return compareNumbers(len, m.val, m.op)
      }
      case "length-value": {
        const m = cached.meta as { path: string }
        const val = this.state.get(m.path)
        return Array.isArray(val) ? val.length : 0
      }
      case "simple-key": {
        const val = this.state.get(expr)
        if (val !== undefined) return val
        return this.state.resolve(expr)
      }
      default:
        // Complex or uncached expressions — re-evaluate fully
        return this.evaluateAndCache(expr)
    }
  }

  /**
   * Core evaluation logic — the original evaluate() body after unwrap.
   * Caches the expression classification at simple/common branches.
   */
  private evaluateAndCache(trimmed: string): unknown {
    // 0b. String literal: 'text' or "text"
    if (
      (trimmed.startsWith("'") && trimmed.endsWith("'")) ||
      (trimmed.startsWith('"') && trimmed.endsWith('"'))
    ) {
      this.cache.set(trimmed, { type: "string-literal" })
      return trimmed.substring(1, trimmed.length - 1)
    }

    // 0c. Numeric literal
    const numLiteral = parseNumberStrict(trimmed)
    if (numLiteral !== null) {
      this.cache.set(trimmed, { type: "number-literal", value: numLiteral })
      return numLiteral
    }

    // 0d. Boolean / null literals
    if (trimmed === "true") {
      this.cache.set(trimmed, { type: "boolean", value: true })
      return true
    }
    if (trimmed === "false") {
      this.cache.set(trimmed, { type: "boolean", value: false })
      return false
    }
    if (trimmed === "null") {
      this.cache.set(trimmed, { type: "null-literal" })
      return null
    }

    // 0e. Number method: expr.toFixed(n) → string with n decimal places
    const toFixedMatch = /^(.+)\.toFixed\((\d+)\)$/.exec(trimmed)
    if (toFixedMatch) {
      const inner = this.evaluate(toFixedMatch[1].trim())
      const digits = parseInt(toFixedMatch[2], 10)
      return toNum(inner).toFixed(digits)
    }

    // 0f. String/Number method: expr.toString()
    const toStringMatch = /^(.+)\.toString\(\)$/.exec(trimmed)
    if (toStringMatch) {
      const inner = this.evaluate(toStringMatch[1].trim())
      return inner != null ? String(inner) : ""
    }

    // 0g. String methods: expr.toUpperCase(), expr.toLowerCase(), expr.trim()
    const stringNoArgMatch = /^(.+)\.(toUpperCase|toLowerCase|trim)\(\)$/.exec(trimmed)
    if (stringNoArgMatch) {
      const inner = this.evaluate(stringNoArgMatch[1].trim())
      const str = inner != null ? String(inner) : ""
      switch (stringNoArgMatch[2]) {
        case "toUpperCase": return str.toUpperCase()
        case "toLowerCase": return str.toLowerCase()
        case "trim": return str.trim()
      }
    }

    // 0h. String methods with 1 arg: expr.includes('x'), expr.startsWith('x'), expr.endsWith('x')
    // Skip when expression contains "=>" (arrow function) — those are filter/find/map lambdas
    // handled by the array method handlers at step 4+.
    const stringOneArgMatch = /^(.+)\.(includes|startsWith|endsWith)\((.+)\)$/.exec(trimmed)
    if (stringOneArgMatch && !trimmed.includes("=>")) {
      const inner = this.evaluate(stringOneArgMatch[1].trim())
      const str = inner != null ? String(inner) : ""
      const arg = this.evaluate(stringOneArgMatch[3].trim())
      const argStr = arg != null ? String(arg) : ""
      switch (stringOneArgMatch[2]) {
        case "includes": return str.includes(argStr)
        case "startsWith": return str.startsWith(argStr)
        case "endsWith": return str.endsWith(argStr)
      }
    }

    // 0i. String replace: expr.replace('a', 'b')
    const replaceMatch = /^(.+)\.replace\((.+),\s*(.+)\)$/.exec(trimmed)
    if (replaceMatch && !trimmed.includes("=>")) {
      const inner = this.evaluate(replaceMatch[1].trim())
      const str = inner != null ? String(inner) : ""
      const search = this.evaluate(replaceMatch[2].trim())
      const replacement = this.evaluate(replaceMatch[3].trim())
      return str.replace(String(search ?? ""), String(replacement ?? ""))
    }

    // 0j. String substring: expr.substring(start, end)
    const substringMatch = /^(.+)\.substring\((\d+)(?:,\s*(\d+))?\)$/.exec(trimmed)
    if (substringMatch) {
      const inner = this.evaluate(substringMatch[1].trim())
      const str = inner != null ? String(inner) : ""
      const start = parseInt(substringMatch[2], 10)
      const end = substringMatch[3] != null ? parseInt(substringMatch[3], 10) : undefined
      return str.substring(start, end)
    }

    // 0k. String split: expr.split('sep')
    const splitMatch = /^(.+)\.split\((.+)\)$/.exec(trimmed)
    if (splitMatch && !trimmed.includes("=>")) {
      const inner = this.evaluate(splitMatch[1].trim())
      const str = inner != null ? String(inner) : ""
      const sep = this.evaluate(splitMatch[2].trim())
      return str.split(String(sep ?? ""))
    }

    // 1. Simple state reference: "#path" (no spaces, parens, brackets, braces)
    if (
      trimmed.startsWith("#") &&
      !trimmed.includes(" ") &&
      !trimmed.includes("(") &&
      !trimmed.includes("[") &&
      !trimmed.includes("{")
    ) {
      this.cache.set(trimmed, { type: "simple-path" })
      return this.state.get(trimmed.substring(1))
    }

    // Simple dotted path like "computed.cartSubtotal" — resolve from state.
    // Exclude paths ending in .length (handled by length handler below).
    if (
      !trimmed.includes(" ") &&
      !trimmed.includes("(") &&
      !trimmed.includes("[") &&
      !trimmed.includes("{") &&
      !trimmed.includes("?") &&
      !trimmed.includes("'") &&
      /^[\w.]+$/.test(trimmed) &&
      trimmed.includes(".") &&
      !trimmed.endsWith(".length")
    ) {
      this.cache.set(trimmed, { type: "dotted-path" })
      return this.state.get(trimmed)
    }

    // 2. Length comparison: "#path.length == 0"
    const lengthCmp = /^#([\w.]+)\.length\s*(==|!=|>|<|>=|<=)\s*(\d+)$/.exec(trimmed)
    if (lengthCmp) {
      const arr = this.state.get(lengthCmp[1])
      const op = lengthCmp[2]
      const val = parseInt(lengthCmp[3], 10)
      const len = Array.isArray(arr) ? arr.length : 0
      this.cache.set(trimmed, { type: "length-compare", meta: { path: lengthCmp[1], op, val } })
      return compareNumbers(len, val, op)
    }

    // 2b. Length as value: "#path.length" or "path.length" (no comparison operator)
    const lengthVal = /^#?([\w.]+)\.length$/.exec(trimmed)
    if (lengthVal) {
      this.cache.set(trimmed, { type: "length-value", meta: { path: lengthVal[1] } })
      const val = this.state.get(lengthVal[1])
      if (Array.isArray(val)) return val.length
      if (typeof val === "string") return val.length
      return 0
    }

    // 3. Unique values extraction: ['prefix', ...new Set(#source.map(p => p.field))]
    const uniqueMatch =
      /\['([^']+)',\s*\.\.\.new Set\(#(\w+(?:\.\w+)*)\.map\(\w+\s*=>\s*\w+\.(\w+)\)\)/.exec(trimmed)
    if (uniqueMatch) {
      const prefix = uniqueMatch[1]
      const source = this.state.get(uniqueMatch[2])
      const field = uniqueMatch[3]
      if (Array.isArray(source)) {
        const valuesSet = new Set<string>()
        for (const item of source) {
          if (item && typeof item === "object") {
            const v = (item as Record<string, unknown>)[field]
            if (v != null && String(v).length > 0) {
              valuesSet.add(String(v))
            }
          }
        }
        const values = Array.from(valuesSet).sort()
        return [prefix, ...values]
      }
      return [prefix]
    }

    // 3b. Nullish coalescing: expr ?? fallback (checked early so find/filter chains can return null)
    {
      const nIdx = findTopLevelStr(trimmed, "??")
      if (nIdx > 0) {
        const leftExpr = trimmed.substring(0, nIdx).trim()
        const rightExpr = trimmed.substring(nIdx + 2).trim()
        // For simple paths, check state.get directly to detect null/undefined
        const pathLike = leftExpr.replace(/^#/, "")
        let leftVal: unknown
        if (/^[\w.]+$/.test(pathLike)) {
          leftVal = this.state.get(pathLike)
        } else {
          leftVal = this.evaluate(leftExpr)
        }
        if (leftVal === null || leftVal === undefined) {
          return this.evaluate(rightExpr)
        }
        return leftVal
      }
    }

    // 3c-pre. Ternary: expr ? valueA : valueB — must come BEFORE arithmetic split
    //         so "cond ? a + b : c" doesn't split on the + inside ternary branches.
    const ternaryResult = this.tryTernary(trimmed)
    if (ternaryResult !== undefined) return ternaryResult

    // 3c. Top-level arithmetic split (+, -) — must come BEFORE filter/find chains
    //     so "expr.filter(...).length + expr2" splits correctly.
    for (const op of ["+", "-"]) {
      const idx = findLastTopLevelChar(trimmed, op)
      if (idx > 0) {
        const left = this.evaluate(trimmed.substring(0, idx).trim())
        const right = this.evaluate(trimmed.substring(idx + 1).trim())
        // String concatenation via + (matching Flutter/Dart behavior)
        if (op === "+" && (typeof left === "string" || typeof right === "string")) {
          return `${left ?? ""}${right ?? ""}`
        }
        const ln = toNum(left), rn = toNum(right)
        return op === "+" ? ln + rn : ln - rn
      }
    }

    // 4. Array filter: #source.filter(p => condition) or source.filter(...)
    //    With optional chaining: .filter(...).length, .filter(...).property
    const filterChainResult = this.tryFilterWithChain(trimmed)
    if (filterChainResult !== undefined) return filterChainResult

    // 5. Array find: #source.find(p => condition).property or source.find(...)
    //    With optional chaining: .find(...).property
    const findChainResult = this.tryFindWithChain(trimmed)
    if (findChainResult !== undefined) return findChainResult

    // 5b. Array sort: #source.sort() with optional .join() chaining
    const sortResult = this.trySortExpression(trimmed)
    if (sortResult !== undefined) return sortResult

    // 5b2. Array slice: #source.slice(start, end) or source.slice(start)
    const sliceResult = this.trySliceExpression(trimmed)
    if (sliceResult !== undefined) return sliceResult

    // 5c. Array join: #source.join('sep')
    const joinResult = this.tryJoinExpression(trimmed)
    if (joinResult !== undefined) return joinResult

    // 6a. Array reduce
    const reduceResult = this.tryReduceExpression(trimmed)
    if (reduceResult !== undefined) return reduceResult

    // 6b. Array map with object construction
    const mapResult = this.tryMapExpression(trimmed)
    if (mapResult !== undefined) return mapResult

    // 6c. Logical AND/OR: expr1 && expr2, expr1 || expr2
    // Must come before ternary/comparison to correctly parse compound boolean expressions
    // like "product.price > 0 && product.originalPrice != null".
    const andIdx = findTopLevelStr(trimmed, "&&")
    if (andIdx > 0) {
      const left = this.evaluate(trimmed.substring(0, andIdx).trim())
      if (!isTruthy(left)) return false
      const right = this.evaluate(trimmed.substring(andIdx + 2).trim())
      return isTruthy(right)
    }
    const orIdx = findTopLevelStr(trimmed, "||")
    if (orIdx > 0) {
      const left = this.evaluate(trimmed.substring(0, orIdx).trim())
      if (isTruthy(left)) return true
      const right = this.evaluate(trimmed.substring(orIdx + 2).trim())
      return isTruthy(right)
    }

    // 6d. Negation: !expr  (after &&/|| so precedence is correct)
    if (trimmed.startsWith("!")) {
      const inner = trimmed.substring(1).trim()
      const result = this.evaluate(inner)
      return !isTruthy(result)
    }

    // 6e. Comparison: #a >= b, #a <= b, etc.
    const cmpResult = this.tryComparison(trimmed)
    if (cmpResult !== undefined) return cmpResult

    // 6. Math.max / Math.min / Math.round / Math.floor / Math.ceil / Math.abs
    const mathMatch = /^Math\.(max|min|round|floor|ceil|abs)\((.+)\)$/.exec(trimmed)
    if (mathMatch) {
      const fn = mathMatch[1]
      const argsStr = mathMatch[2]
      const args = splitArgs(argsStr).map((a) => toNum(this.evaluate(a.trim())))
      switch (fn) {
        case "max":
          return args.length >= 2 ? Math.max(args[0], args[1]) : (args[0] ?? 0)
        case "min":
          return args.length >= 2 ? Math.min(args[0], args[1]) : (args[0] ?? 0)
        case "round":
          return args.length > 0 ? Math.round(args[0]) : 0
        case "floor":
          return args.length > 0 ? Math.floor(args[0]) : 0
        case "ceil":
          return args.length > 0 ? Math.ceil(args[0]) : 0
        case "abs":
          return args.length > 0 ? Math.abs(args[0]) : 0
      }
    }

    // 7. formatCurrency(expr) -> "$X.XX"
    const currencyMatch = /^formatCurrency\((.+)\)$/.exec(trimmed)
    if (currencyMatch) {
      const inner = currencyMatch[1].trim()
      const value = toNum(this.evaluate(inner))
      return `$${value.toFixed(2)}`
    }

    // 7b. formatCrypto(rawAmount, decimals, symbol?) -> "1.5 ETH"
    const formatCryptoMatch = /^formatCrypto\((.+)\)$/.exec(trimmed)
    if (formatCryptoMatch) {
      const args = splitArgs(formatCryptoMatch[1])
      const raw = toNum(this.evaluate(args[0].trim()))
      const decimals = args.length > 1 ? toNum(this.evaluate(args[1].trim())) : 0
      const symbol = args.length > 2 ? String(this.evaluate(args[2].trim())) : ""
      const converted = decimals > 0 ? raw / Math.pow(10, decimals) : raw
      const maxDp = Math.min(decimals || 8, 8)
      const formatted = converted.toFixed(maxDp).replace(/\.?0+$/, "")
      return symbol ? `${formatted} ${symbol}` : formatted
    }

    // 7c. fromWei(value, decimals?) -> number
    const fromWeiMatch = /^fromWei\((.+)\)$/.exec(trimmed)
    if (fromWeiMatch) {
      const args = splitArgs(fromWeiMatch[1])
      const wei = toNum(this.evaluate(args[0].trim()))
      const decimals = args.length > 1 ? toNum(this.evaluate(args[1].trim())) : 18
      return wei / Math.pow(10, decimals)
    }

    // 7d. toWei(amount, decimals?) -> string
    const toWeiMatch = /^toWei\((.+)\)$/.exec(trimmed)
    if (toWeiMatch) {
      const args = splitArgs(toWeiMatch[1])
      const amount = toNum(this.evaluate(args[0].trim()))
      const decimals = args.length > 1 ? toNum(this.evaluate(args[1].trim())) : 18
      return String(Math.round(amount * Math.pow(10, decimals)))
    }

    // 7e. shortenAddress(address, chars?) -> "0x1234...5678"
    const shortenMatch = /^shortenAddress\((.+)\)$/.exec(trimmed)
    if (shortenMatch) {
      const args = splitArgs(shortenMatch[1])
      const addr = String(this.evaluate(args[0].trim()) ?? "")
      const chars = args.length > 1 ? toNum(this.evaluate(args[1].trim())) : 4
      if (addr.length <= chars * 2 + 2) return addr
      return `${addr.substring(0, chars + 2)}...${addr.substring(addr.length - chars)}`
    }

    // 7f. formatNumber(n, decimals?) -> "1,234,567.89"
    const formatNumMatch = /^formatNumber\((.+)\)$/.exec(trimmed)
    if (formatNumMatch) {
      const args = splitArgs(formatNumMatch[1])
      const val = toNum(this.evaluate(args[0].trim()))
      if (args.length > 1) {
        const decimals = toNum(this.evaluate(args[1].trim()))
        return val.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
      }
      return val.toLocaleString("en-US")
    }

    // 7g. timeAgo(timestamp) -> "3 min ago"
    const timeAgoMatch = /^timeAgo\((.+)\)$/.exec(trimmed)
    if (timeAgoMatch) {
      const ts = toNum(this.evaluate(timeAgoMatch[1].trim()))
      const now = Date.now() / 1000
      const diff = Math.abs(now - ts)
      if (diff < 60) return "just now"
      if (diff < 3600) return `${Math.floor(diff / 60)} min ago`
      if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`
      if (diff < 2592000) return `${Math.floor(diff / 86400)} day ago`
      return `${Math.floor(diff / 2592000)} mo ago`
    }

    // 8. Multiplication/division (higher precedence, handled after +/- at step 3c).
    for (const op of ["*", "/"]) {
      const idx = findLastTopLevelChar(trimmed, op)
      if (idx > 0) {
        const left = this.evaluate(trimmed.substring(0, idx).trim())
        const right = this.evaluate(trimmed.substring(idx + 1).trim())
        const ln = toNum(left), rn = toNum(right)
        return op === "*" ? ln * rn : rn !== 0 ? ln / rn : 0
      }
    }

    // 8b. Parenthesized expression: (expr) — strip parens and recurse.
    if (trimmed.startsWith("(") && trimmed.endsWith(")")) {
      // Verify the closing paren matches the opening one (not nested)
      let depth = 0
      let balanced = true
      for (let i = 0; i < trimmed.length - 1; i++) {
        if (trimmed[i] === "(") depth++
        if (trimmed[i] === ")") depth--
        if (depth === 0) { balanced = false; break }
      }
      if (balanced) {
        return this.evaluate(trimmed.substring(1, trimmed.length - 1))
      }
    }

    // 9. Simple key (no dots, no spaces, no special chars) — state lookup
    if (/^\w+$/.test(trimmed)) {
      const val = this.state.get(trimmed)
      if (val !== undefined) {
        this.cache.set(trimmed, { type: "simple-key" })
        return val // return null for explicitly-null values
      }
    }

    // 10. Fall back to resolve (handles #{path} and interpolation)
    return this.state.resolve(trimmed)
  }

  // ---- Filter Condition ----

  /** Evaluate a filter condition against an item. */
  evalFilterCondition(condition: string, paramName: string, item: unknown): boolean {
    const itemMap =
      item && typeof item === "object" ? (item as Record<string, unknown>) : {}

    // Split by && (top level)
    if (condition.includes("&&")) {
      const parts = splitLogicalParts(condition, "&&")
      if (parts) {
        return (
          this.evalFilterCondition(parts[0], paramName, item) &&
          this.evalFilterCondition(parts[1], paramName, item)
        )
      }
    }

    // Split by || (top level)
    if (condition.includes("||")) {
      const parts = splitLogicalParts(condition, "||")
      if (parts) {
        return (
          this.evalFilterCondition(parts[0], paramName, item) ||
          this.evalFilterCondition(parts[1], paramName, item)
        )
      }
    }

    let expr = condition.trim()

    // Remove outer parens
    while (expr.startsWith("(") && expr.endsWith(")") && matchingParen(expr)) {
      expr = expr.substring(1, expr.length - 1).trim()
    }

    // Recursion with simplified expr
    if (expr !== condition.trim()) {
      return this.evalFilterCondition(expr, paramName, item)
    }

    // Negation: !expr
    if (expr.startsWith("!")) {
      const inner = expr.substring(1).trim()
      if (inner.startsWith("#")) {
        const val = this.state.get(inner.substring(1))
        return !isTruthy(val)
      }
      return !this.evalFilterCondition(inner, paramName, item)
    }

    // .includes() pattern: a.toLowerCase().includes(b.toLowerCase())
    const includesMatch =
      /(\w+\.\w+(?:\.\w+)*)\.toLowerCase\(\)\.includes\((.+)\.toLowerCase\(\)\)/.exec(expr)
    if (includesMatch) {
      const leftPath = includesMatch[1]
      const rightPath = includesMatch[2]
      const leftVal = this.resolveFilterValue(leftPath, paramName, itemMap)
      const rightVal = this.resolveFilterValue(rightPath, paramName, itemMap)
      const leftStr = (leftVal ?? "").toString().toLowerCase()
      const rightStr = (rightVal ?? "").toString().toLowerCase()
      return leftStr.includes(rightStr)
    }

    // Comparison: a === b, a !== b, a == b, a != b, a >= b, a <= b, a > b, a < b
    for (const op of ["===", "!==", ">=", "<=", "==", "!=", ">", "<"]) {
      const opIdx = expr.indexOf(op)
      if (opIdx > 0) {
        const leftStr = expr.substring(0, opIdx).trim()
        const rightStr = expr.substring(opIdx + op.length).trim()
        const left = this.resolveFilterValue(leftStr, paramName, itemMap)
        const right = this.resolveFilterValue(rightStr, paramName, itemMap)
        if (op === "===" || op === "==") return left === right || String(left) === String(right)
        if (op === "!==" || op === "!=") return left !== right && String(left) !== String(right)
        if (op === ">=") return String(left) >= String(right)
        if (op === "<=") return String(left) <= String(right)
        if (op === ">") return String(left) > String(right)
        if (op === "<") return String(left) < String(right)
      }
    }

    // Truthy check for state ref: #path
    if (expr.startsWith("#")) {
      return isTruthy(this.state.get(expr.substring(1)))
    }

    // Truthy check for item property: paramName.prop
    if (expr.startsWith(`${paramName}.`)) {
      const prop = expr.substring(paramName.length + 1)
      const val = getNestedValue(itemMap, prop)
      return isTruthy(val)
    }

    return true // default pass
  }

  /** Resolve a value in filter context: handles item props, state refs, and literals. */
  resolveFilterValue(
    path: string,
    paramName: string,
    itemMap: Record<string, unknown>,
  ): unknown {
    const trimmed = path.trim()

    // Quoted string literal
    if (
      (trimmed.startsWith("'") && trimmed.endsWith("'")) ||
      (trimmed.startsWith('"') && trimmed.endsWith('"'))
    ) {
      return trimmed.substring(1, trimmed.length - 1)
    }

    // Number literal
    const asNum = parseNumberStrict(trimmed)
    if (asNum !== null) return asNum

    // State reference: #path
    if (trimmed.startsWith("#")) {
      return this.state.get(trimmed.substring(1))
    }

    // Item property: paramName.prop
    if (trimmed.startsWith(`${paramName}.`)) {
      const prop = trimmed.substring(paramName.length + 1)
      return getNestedValue(itemMap, prop)
    }

    // State path without # prefix: state.xxx or computed.xxx
    if (trimmed.startsWith("state.") || trimmed.startsWith("computed.")) {
      return this.state.get(trimmed)
    }

    return trimmed
  }

  // ---- Filter with Chain ----

  /**
   * Parse filter expressions with optional chaining:
   *   #source.filter(p => condition)            → returns array
   *   #source.filter(p => condition).length     → returns count
   *   #source.filter(p => condition).property   → returns property of first match
   */
  private tryFilterWithChain(expr: string): unknown {
    // Find .filter( with proper paren matching
    const filterStart = expr.search(/\.filter\(/)
    if (filterStart < 0) return undefined

    const sourcePath = expr.substring(0, filterStart).replace(/^#/, "")
    const afterFilter = expr.substring(filterStart + 8) // skip ".filter("

    // Find matching closing paren for .filter(...)
    let depth = 1
    let i = 0
    for (; i < afterFilter.length && depth > 0; i++) {
      if (afterFilter[i] === "(") depth++
      if (afterFilter[i] === ")") depth--
    }
    if (depth !== 0) return undefined

    const filterBody = afterFilter.substring(0, i - 1) // content inside filter()
    const afterClose = afterFilter.substring(i) // everything after the closing paren

    // Parse the lambda: paramName => condition
    const lambdaMatch = /^(\w+)\s*=>\s*(.+)$/.exec(filterBody.trim())
    if (!lambdaMatch) return undefined

    const paramName = lambdaMatch[1]
    const condition = lambdaMatch[2]
    const source = this.state.get(sourcePath)
    if (!Array.isArray(source)) {
      // If chaining .length, return 0; otherwise return []
      if (afterClose.startsWith(".length")) return 0
      return []
    }

    const filtered = source.filter((item) => this.evalFilterCondition(condition, paramName, item))

    // Check for chaining after filter()
    if (afterClose === "" || afterClose.trim() === "") return filtered
    if (afterClose.startsWith(".length")) return filtered.length
    if (afterClose.startsWith(".")) {
      const prop = afterClose.substring(1)
      if (filtered.length > 0 && typeof filtered[0] === "object") {
        return getNestedValue(filtered[0] as Record<string, unknown>, prop) ?? null
      }
      return null
    }

    return filtered
  }

  // ---- Find with Chain ----

  /**
   * Parse find expressions with optional chaining:
   *   #source.find(p => condition)              → returns found item or null
   *   #source.find(p => condition).property     → returns property of found item
   */
  private tryFindWithChain(expr: string): unknown {
    const findStart = expr.search(/\.find\(/)
    if (findStart < 0) return undefined

    const sourcePath = expr.substring(0, findStart).replace(/^#/, "")
    const afterFind = expr.substring(findStart + 6) // skip ".find("

    let depth = 1
    let i = 0
    for (; i < afterFind.length && depth > 0; i++) {
      if (afterFind[i] === "(") depth++
      if (afterFind[i] === ")") depth--
    }
    if (depth !== 0) return undefined

    const findBody = afterFind.substring(0, i - 1)
    const afterClose = afterFind.substring(i)

    const lambdaMatch = /^(\w+)\s*=>\s*(.+)$/.exec(findBody.trim())
    if (!lambdaMatch) return undefined

    const paramName = lambdaMatch[1]
    const condition = lambdaMatch[2]
    const source = this.state.get(sourcePath)
    if (!Array.isArray(source)) return null

    const found = source.find((item) => this.evalFilterCondition(condition, paramName, item)) ?? null

    // Check for property chaining after find()
    if (afterClose === "" || afterClose.trim() === "") return found
    if (afterClose.startsWith(".") && found && typeof found === "object") {
      const prop = afterClose.substring(1)
      return getNestedValue(found as Record<string, unknown>, prop) ?? null
    }

    return found
  }

  // ---- Sort ----

  private trySortExpression(expr: string): unknown {
    // #source.sort() with optional .join('sep')
    const sortMatch = /^#?(\w+(?:\.\w+)*)\.sort\(\)(.*)$/.exec(expr)
    if (!sortMatch) return undefined

    const source = this.state.get(sortMatch[1])
    if (!Array.isArray(source)) return []

    const sorted = [...source].sort((a, b) => String(a).localeCompare(String(b)))
    const afterSort = sortMatch[2].trim()

    // Check for .join() chaining
    const joinChainMatch = /^\.join\((.+)\)$/.exec(afterSort)
    if (joinChainMatch) {
      const sep = this.evaluate(joinChainMatch[1].trim())
      return sorted.join(String(sep ?? ""))
    }

    if (afterSort === "") return sorted
    return sorted
  }

  // ---- Slice ----

  private trySliceExpression(expr: string): unknown {
    // source.slice(start) or source.slice(start, end)
    const sliceStart = expr.search(/\.slice\(/)
    if (sliceStart === -1) return undefined

    const sourcePath = expr.substring(0, sliceStart).replace(/^#/, "")
    const source = this.state.get(sourcePath)
    if (!Array.isArray(source)) return undefined

    const afterSlice = expr.substring(sliceStart + 7) // skip ".slice("
    // Find matching closing paren
    let depth = 1
    let i = 0
    for (; i < afterSlice.length && depth > 0; i++) {
      if (afterSlice[i] === "(") depth++
      if (afterSlice[i] === ")") depth--
    }
    if (depth !== 0) return undefined

    const argsStr = afterSlice.substring(0, i - 1).trim()
    const args = argsStr.split(",").map((a) => toNum(this.evaluate(a.trim())))
    const start = typeof args[0] === "number" ? args[0] : 0
    const end = args.length > 1 && typeof args[1] === "number" ? args[1] : undefined

    return source.slice(start, end)
  }

  // ---- Join ----

  private tryJoinExpression(expr: string): unknown {
    const joinMatch = /^#?(\w+(?:\.\w+)*)\.join\((.+)\)$/.exec(expr)
    if (!joinMatch) return undefined

    const source = this.state.get(joinMatch[1])
    if (!Array.isArray(source)) return ""

    const sep = this.evaluate(joinMatch[2].trim())
    return source.join(String(sep ?? ""))
  }

  /**
   * Evaluate a predicate string like "item => item.id == #{product.id}".
   * Used by ActionFlowEngine for removeWhere, updateWhere, upsertWhere match predicates.
   */
  evalPredicate(
    predicate: string,
    item: unknown,
    context?: Record<string, unknown>,
  ): boolean {
    const trimmed = predicate.trim()

    // Parse "paramName => expression"
    const arrowMatch = /^(\w+)\s*=>\s*(.+)$/.exec(trimmed)
    if (!arrowMatch) {
      // No arrow function syntax — detect implicit item reference.
      // Predicates like "item.id === 'coffee-1'" use "item" as implicit paramName.
      // Extract the first dot-path identifier and use it as paramName for evalFilterCondition.
      const implicitParam = /^(\w+)\./.exec(trimmed)
      if (implicitParam) {
        return this.evalFilterCondition(trimmed, implicitParam[1], item)
      }
      // Fallback: evaluate as a raw expression (already resolved)
      return isTruthy(this.evaluate(trimmed))
    }

    const paramName = arrowMatch[1]
    let body = arrowMatch[2].trim()

    // Resolve #{...} patterns from context/state
    body = body.replace(/#\{([^}]+)\}/g, (_, ref) => {
      const r = (ref as string).trim()
      // Try context first
      if (context) {
        const ctxVal = getNestedValue(context, r)
        if (ctxVal !== undefined) {
          if (typeof ctxVal === "string") return JSON.stringify(ctxVal)
          return String(ctxVal)
        }
      }
      // Fall back to state
      const stateVal = this.state.get(r)
      if (stateVal === null || stateVal === undefined) return "null"
      if (typeof stateVal === "string") return JSON.stringify(stateVal)
      return String(stateVal)
    })

    // Now evaluate using filter condition logic
    return this.evalFilterCondition(body, paramName, item)
  }

  // ---- Reduce ----

  private tryReduceExpression(expr: string): unknown {
    const match = /^#?(\w+(?:\.\w+)*)\.reduce\(\((\w+),\s*(\w+)\)\s*=>\s*/.exec(expr)
    if (!match) return undefined

    const sourcePath = match[1]
    const accName = match[2]
    const itemName = match[3]
    const rest = expr.substring(match[0].length)

    const lastParen = rest.lastIndexOf(")")
    if (lastParen < 0) return undefined
    const inner = rest.substring(0, lastParen)

    const lastComma = findLastTopLevelComma(inner)
    if (lastComma < 0) return undefined

    const body = inner.substring(0, lastComma).trim()
    const initialStr = inner.substring(lastComma + 1).trim()

    const source = this.state.get(sourcePath)
    if (!Array.isArray(source)) return toNum(this.evaluate(initialStr))

    let accumulator: unknown = this.evaluate(initialStr)

    for (const item of source) {
      const itemMapVal = item && typeof item === "object" ? item : {}
      accumulator = this.evalReduceBody(body, accName, accumulator, itemName, itemMapVal)
    }
    return accumulator
  }

  private evalReduceBody(
    body: string,
    accName: string,
    accValue: unknown,
    itemName: string,
    itemMap: unknown,
  ): unknown {
    let processed = body

    // Replace item.field references with actual values
    const itemRe = new RegExp(`${itemName}\\.(\\w+)`, "g")
    processed = processed.replace(itemRe, (_, field) => {
      const val =
        itemMap && typeof itemMap === "object"
          ? (itemMap as Record<string, unknown>)[field]
          : null
      if (typeof val === "number") return val.toString()
      if (typeof val === "string") {
        const parsed = parseFloat(val)
        return isNaN(parsed) ? "0" : parsed.toString()
      }
      return "0"
    })

    // Replace accumulator reference
    processed = processed.split(accName).join(String(accValue))

    // Evaluate the resulting arithmetic expression
    return evalArithmetic(processed)
  }

  // ---- Map with Object Construction ----

  private tryMapExpression(expr: string): unknown {
    const match = /^#?(\w+(?:\.\w+)*)\.map\((\w+)\s*=>\s*\(\{/.exec(expr)
    if (!match) return undefined

    const sourcePath = match[1]
    const paramName = match[2]

    const bodyStart = expr.indexOf("({") + 2
    const bodyEnd = expr.lastIndexOf("})")
    if (bodyEnd < bodyStart) return undefined

    const objectBody = expr.substring(bodyStart, bodyEnd).trim()

    const source = this.state.get(sourcePath)
    if (!Array.isArray(source)) return []

    return source.map((item) => {
      const itemMap = toStringKeyMap(item)
      const result: Record<string, unknown> = {}

      // Handle spread: ...paramName
      if (objectBody.includes(`...${paramName}`)) {
        Object.assign(result, itemMap)
      }

      // Parse key: expr pairs after spread
      const pairsStr = objectBody.replace(new RegExp(`\\.\\.\\.\\w+,?\\s*`), "")
      const pairs = splitObjectPairs(pairsStr)

      for (const pair of pairs) {
        const colonIdx = pair.indexOf(":")
        if (colonIdx < 0) continue
        const key = pair.substring(0, colonIdx).trim()
        const valueExpr = pair.substring(colonIdx + 1).trim()
        result[key] = this.evalMapItemExpr(valueExpr, paramName, itemMap)
      }

      return result
    })
  }

  private evalMapItemExpr(
    expr: string,
    paramName: string,
    itemMap: Record<string, unknown>,
  ): unknown {
    const trimmed = expr.trim()

    // formatCurrency(item.expr)
    const currMatch = /^formatCurrency\((.+)\)$/.exec(trimmed)
    if (currMatch) {
      const inner = this.evalMapItemExpr(currMatch[1].trim(), paramName, itemMap)
      const val = toNum(inner)
      return `$${val.toFixed(2)}`
    }

    // Ternary: condition ? valA : valB
    const ternIdx = findTopLevelChar(trimmed, "?")
    if (ternIdx > 0) {
      const colonIdx = findTopLevelChar(trimmed.substring(ternIdx + 1), ":")
      if (colonIdx > 0) {
        const condition = trimmed.substring(0, ternIdx).trim()
        const trueVal = trimmed.substring(ternIdx + 1, ternIdx + 1 + colonIdx).trim()
        const falseVal = trimmed.substring(ternIdx + 1 + colonIdx + 1).trim()
        const condResult = this.evalMapItemCondition(condition, paramName, itemMap)
        return condResult
          ? this.evalMapItemExpr(trueVal, paramName, itemMap)
          : this.evalMapItemExpr(falseVal, paramName, itemMap)
      }
    }

    // Arithmetic: item.field * item.field2
    for (const op of ["*", "+", "-", "/"]) {
      const opIdx = findTopLevelChar(trimmed, op)
      if (opIdx > 0) {
        const left = this.evalMapItemExpr(trimmed.substring(0, opIdx).trim(), paramName, itemMap)
        const right = this.evalMapItemExpr(trimmed.substring(opIdx + 1).trim(), paramName, itemMap)
        const l = toNum(left)
        const r = toNum(right)
        switch (op) {
          case "*": return l * r
          case "+": return l + r
          case "-": return l - r
          case "/": return r !== 0 ? l / r : 0
        }
      }
    }

    // Item property: item.field
    if (trimmed.startsWith(`${paramName}.`)) {
      return getNestedValue(itemMap, trimmed.substring(paramName.length + 1))
    }

    // String literal
    if (
      (trimmed.startsWith("'") && trimmed.endsWith("'")) ||
      (trimmed.startsWith('"') && trimmed.endsWith('"'))
    ) {
      return trimmed.substring(1, trimmed.length - 1)
    }

    // Number literal
    const asNum = parseNumberStrict(trimmed)
    if (asNum !== null) return asNum

    // null literal
    if (trimmed === "null") return null

    // State reference
    if (trimmed.startsWith("#")) {
      return this.state.get(trimmed.substring(1))
    }

    return trimmed
  }

  private evalMapItemCondition(
    condition: string,
    paramName: string,
    itemMap: Record<string, unknown>,
  ): boolean {
    const trimmed = condition.trim()

    // item.field != null
    const neqNull = /^(\w+\.\w+)\s*!=\s*null$/.exec(trimmed)
    if (neqNull) {
      const val = this.resolveMapItemPath(neqNull[1], paramName, itemMap)
      return val != null
    }

    // Comparison operators
    for (const op of [">=", "<=", ">", "<", "==", "!="]) {
      const opIdx = trimmed.indexOf(op)
      if (opIdx > 0) {
        const left = this.evalMapItemExpr(trimmed.substring(0, opIdx).trim(), paramName, itemMap)
        const right = this.evalMapItemExpr(
          trimmed.substring(opIdx + op.length).trim(),
          paramName,
          itemMap,
        )
        return compareValues(left, right, op)
      }
    }

    // && and ||
    const andIdx = findTopLevelStr(trimmed, "&&")
    if (andIdx > 0) {
      return (
        this.evalMapItemCondition(trimmed.substring(0, andIdx).trim(), paramName, itemMap) &&
        this.evalMapItemCondition(trimmed.substring(andIdx + 2).trim(), paramName, itemMap)
      )
    }

    // Truthy check
    const val = this.evalMapItemExpr(trimmed, paramName, itemMap)
    return isTruthy(val)
  }

  private resolveMapItemPath(
    path: string,
    paramName: string,
    itemMap: Record<string, unknown>,
  ): unknown {
    if (path.startsWith(`${paramName}.`)) {
      return getNestedValue(itemMap, path.substring(paramName.length + 1))
    }
    if (path.startsWith("#")) return this.state.get(path.substring(1))
    return path
  }

  // ---- Ternary ----

  private tryTernary(expr: string): unknown {
    const qIdx = findTopLevelChar(expr, "?")
    if (qIdx <= 0) return undefined

    const condition = expr.substring(0, qIdx).trim()
    const rest = expr.substring(qIdx + 1)
    const cIdx = findTopLevelChar(rest, ":")
    if (cIdx < 0) return undefined

    const trueExpr = rest.substring(0, cIdx).trim()
    const falseExpr = rest.substring(cIdx + 1).trim()

    const condVal = this.evaluate(condition)
    return isTruthy(condVal) ? this.evaluate(trueExpr) : this.evaluate(falseExpr)
  }

  // ---- Comparison ----

  private tryComparison(expr: string): unknown {
    // Check multi-char operators first (longest match), then single-char, all at top level (depth 0).
    // IMPORTANT: === and !== must come BEFORE == and != to avoid partial matching.
    for (const op of ["===", "!==", ">=", "<=", "!=", "==", ">", "<"]) {
      const idx = op.length > 1
        ? findTopLevelStr(expr, op)
        : findTopLevelChar(expr, op)
      if (idx > 0) {
        // Ensure single-char > or < isn't part of >= or <=
        if (op === ">" && idx + 1 < expr.length && expr[idx + 1] === "=") continue
        if (op === "<" && idx + 1 < expr.length && expr[idx + 1] === "=") continue

        const left = this.evaluate(expr.substring(0, idx).trim())
        const right = this.evaluate(expr.substring(idx + op.length).trim())
        return compareValues(left, right, op)
      }
    }
    return undefined
  }
}

// ---- Utility Functions ----

/** Check if a value is truthy (matches Flutter's CogoStateManager.isTruthy). */
export function isTruthy(val: unknown): boolean {
  if (val === null || val === undefined) return false
  if (typeof val === "boolean") return val
  if (typeof val === "number") return val !== 0
  if (typeof val === "string") return val.length > 0
  if (Array.isArray(val)) return val.length > 0
  return true
}

function compareValues(left: unknown, right: unknown, op: string): boolean {
  switch (op) {
    case "===":
    case "==":
      return left === right || String(left) === String(right)
    case "!==":
    case "!=":
      return left !== right && String(left) !== String(right)
    case ">":
      return typeof left === "number" && typeof right === "number" ? left > right : false
    case "<":
      return typeof left === "number" && typeof right === "number" ? left < right : false
    case ">=":
      return typeof left === "number" && typeof right === "number" ? left >= right : false
    case "<=":
      return typeof left === "number" && typeof right === "number" ? left <= right : false
  }
  return false
}

function compareNumbers(left: number, right: number, op: string): boolean {
  switch (op) {
    case "==": return left === right
    case "!=": return left !== right
    case ">": return left > right
    case "<": return left < right
    case ">=": return left >= right
    case "<=": return left <= right
  }
  return false
}

/** Convert unknown to number safely. */
export function toNum(v: unknown): number {
  if (typeof v === "number") return v
  if (typeof v === "string") {
    const n = parseFloat(v)
    return isNaN(n) ? 0 : n
  }
  return 0
}

function parseNumberStrict(s: string): number | null {
  if (s === "") return null
  // Match integer or float patterns
  if (/^-?\d+(\.\d+)?$/.test(s)) {
    const n = Number(s)
    return isNaN(n) ? null : n
  }
  return null
}

function getNestedValue(obj: unknown, path: string): unknown {
  const keys = path.split(".")
  let current: unknown = obj
  for (const key of keys) {
    if (current == null || typeof current !== "object") return undefined
    current = (current as Record<string, unknown>)[key]
  }
  return current
}

function toStringKeyMap(item: unknown): Record<string, unknown> {
  if (!item || typeof item !== "object") return {}
  return item as Record<string, unknown>
}

/** Split comma-separated args respecting parentheses. */
export function splitArgs(s: string): string[] {
  const result: string[] = []
  let depth = 0
  let start = 0
  for (let i = 0; i < s.length; i++) {
    if (s[i] === "(") depth++
    if (s[i] === ")") depth--
    if (s[i] === "," && depth === 0) {
      result.push(s.substring(start, i))
      start = i + 1
    }
  }
  result.push(s.substring(start))
  return result
}

/** Split expression by a logical operator at top level (not inside parens). */
export function splitLogicalParts(expr: string, op: string): [string, string] | null {
  let depth = 0
  for (let i = 0; i <= expr.length - op.length; i++) {
    if (expr[i] === "(") depth++
    if (expr[i] === ")") depth--
    if (depth === 0 && expr.substring(i, i + op.length) === op) {
      return [expr.substring(0, i).trim(), expr.substring(i + op.length).trim()]
    }
  }
  return null
}

/** Check if outer parens match. */
export function matchingParen(expr: string): boolean {
  let depth = 0
  for (let i = 0; i < expr.length; i++) {
    if (expr[i] === "(") depth++
    if (expr[i] === ")") depth--
    if (depth === 0 && i < expr.length - 1) return false
  }
  return depth === 0
}

/** Find a character at top level (not inside parens/braces/quotes). */
function findTopLevelChar(s: string, ch: string): number {
  let depth = 0
  let inSingleQuote = false
  let inDoubleQuote = false
  for (let i = 0; i < s.length; i++) {
    if (s[i] === "'" && !inDoubleQuote) inSingleQuote = !inSingleQuote
    if (s[i] === '"' && !inSingleQuote) inDoubleQuote = !inDoubleQuote
    if (inSingleQuote || inDoubleQuote) continue
    if (s[i] === "(" || s[i] === "{") depth++
    if (s[i] === ")" || s[i] === "}") depth--
    if (depth === 0 && s[i] === ch) return i
  }
  return -1
}

/** Find the LAST occurrence of a single-char operator at top level (depth 0).
 *  Right-to-left scan ensures correct operator precedence.
 *  Respects string literals (single/double quotes) to avoid matching inside strings. */
function findLastTopLevelChar(s: string, ch: string): number {
  let depth = 0
  let inSingle = false
  let inDouble = false
  for (let i = s.length - 1; i > 0; i--) {
    if (s[i] === "'" && !inDouble) inSingle = !inSingle
    if (s[i] === '"' && !inSingle) inDouble = !inDouble
    if (inSingle || inDouble) continue
    if (s[i] === ")" || s[i] === "}") depth++
    if (s[i] === "(" || s[i] === "{") depth--
    if (depth === 0 && s[i] === ch) return i
  }
  return -1
}

/** Find a multi-char string at top level (respects string literals). */
function findTopLevelStr(s: string, target: string): number {
  let depth = 0
  let inSingle = false
  let inDouble = false
  for (let i = 0; i <= s.length - target.length; i++) {
    if (s[i] === "'" && !inDouble) { inSingle = !inSingle; continue }
    if (s[i] === '"' && !inSingle) { inDouble = !inDouble; continue }
    if (inSingle || inDouble) continue
    if (s[i] === "(" || s[i] === "{") depth++
    if (s[i] === ")" || s[i] === "}") depth--
    if (depth === 0 && s.substring(i, i + target.length) === target) return i
  }
  return -1
}

function findLastTopLevelComma(s: string): number {
  let depth = 0
  let braceDepth = 0
  let lastComma = -1
  for (let i = 0; i < s.length; i++) {
    if (s[i] === "(") depth++
    if (s[i] === ")") depth--
    if (s[i] === "{") braceDepth++
    if (s[i] === "}") braceDepth--
    if (s[i] === "," && depth === 0 && braceDepth === 0) {
      lastComma = i
    }
  }
  return lastComma
}

function splitObjectPairs(s: string): string[] {
  const result: string[] = []
  let depth = 0
  let start = 0
  for (let i = 0; i < s.length; i++) {
    if (s[i] === "(" || s[i] === "{") depth++
    if (s[i] === ")" || s[i] === "}") depth--
    if (s[i] === "," && depth === 0) {
      const part = s.substring(start, i).trim()
      if (part.length > 0) result.push(part)
      start = i + 1
    }
  }
  const last = s.substring(start).trim()
  if (last.length > 0) result.push(last)
  return result
}

/**
 * Simple arithmetic evaluator for reduce bodies.
 * Uses recursive descent: lowest-precedence operators (+/-) first,
 * then higher-precedence (* /) so `a + b * c` evaluates correctly.
 */
function evalArithmetic(expr: string): number {
  const trimmed = expr.trim()

  const asNum = parseFloat(trimmed)
  if (!isNaN(asNum) && /^-?\d+(\.\d+)?$/.test(trimmed)) return asNum

  // Handle addition/subtraction first (lowest precedence)
  // Find last top-level + or -
  for (let i = trimmed.length - 1; i > 0; i--) {
    if ((trimmed[i] === "+" || trimmed[i] === "-") && isTopLevel(trimmed, i)) {
      const left = evalArithmetic(trimmed.substring(0, i))
      const right = evalArithmetic(trimmed.substring(i + 1))
      return trimmed[i] === "+" ? left + right : left - right
    }
  }

  // Handle multiplication/division (higher precedence)
  for (let i = trimmed.length - 1; i > 0; i--) {
    if ((trimmed[i] === "*" || trimmed[i] === "/") && isTopLevel(trimmed, i)) {
      const left = evalArithmetic(trimmed.substring(0, i))
      const right = evalArithmetic(trimmed.substring(i + 1))
      return trimmed[i] === "*" ? left * right : (right !== 0 ? left / right : 0)
    }
  }

  // Handle parens
  if (trimmed.startsWith("(") && trimmed.endsWith(")")) {
    return evalArithmetic(trimmed.substring(1, trimmed.length - 1))
  }

  const fallback = parseFloat(trimmed)
  return isNaN(fallback) ? 0 : fallback
}

function isTopLevel(s: string, idx: number): boolean {
  let depth = 0
  for (let i = 0; i < idx; i++) {
    if (s[i] === "(") depth++
    if (s[i] === ")") depth--
  }
  return depth === 0
}
